import { useCallback, useRef, useState } from 'react';
import { api } from '../lib/api';

export interface DuplicateMatch {
  id: string;
  customerCompanyName: string;
  pointOfContact?: { name?: string; phone?: string; email?: string };
  leadStatus?: string;
  createdAt: string;
  matchedOn: ('phone' | 'email' | 'name')[];
}

interface CheckResponse {
  success: boolean;
  data: DuplicateMatch[];
}

/**
 * Hook for checking duplicate leads by phone, email, or name.
 * Returns matches and a check function that can be called on blur / debounced input.
 */
export function useDuplicateLeadCheck(orgId: string | null) {
  const [matches, setMatches] = useState<DuplicateMatch[]>([]);
  const [checking, setChecking] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const checkDuplicates = useCallback(
    async (params: { phone?: string; email?: string; name?: string }) => {
      if (!orgId) return [];
      // Skip if all params are empty/short
      const phone = params.phone?.replace(/\D/g, '') ?? '';
      const email = params.email?.trim() ?? '';
      const name = params.name?.trim() ?? '';
      if (phone.length < 7 && !email && name.length < 2) {
        setMatches([]);
        return [];
      }

      // Abort previous in-flight request
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setChecking(true);
      try {
        const res = await api.post<CheckResponse>(`/quotes/${orgId}/check-duplicate-leads`, {
          ...(phone.length >= 7 ? { phone: params.phone } : {}),
          ...(email ? { email } : {}),
          ...(name.length >= 2 ? { name } : {}),
        });
        if (!controller.signal.aborted) {
          setMatches(res.data);
          return res.data;
        }
      } catch {
        if (!controller.signal.aborted) {
          setMatches([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setChecking(false);
        }
      }
      return [];
    },
    [orgId],
  );

  const clearMatches = useCallback(() => setMatches([]), []);

  return { matches, checking, checkDuplicates, clearMatches };
}
